"use client";

import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface KeyStatItem {
  _key?: string;
  value?: string;
  label?: string;
  icon?: string;
}

export interface KeyStatsSectionData {
  items?: KeyStatItem[];
}

function UserBitcoinIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.5417 25.375V18.125M22.9583 18.125V16.3125M22.9583 27.1875V25.375M20.5417 21.75H24.1667M22.9583 21.75H23.5625M23.5625 21.75C24.0432 21.75 24.5042 21.941 24.8441 22.2809C25.184 22.6208 25.375 23.0818 25.375 23.5625C25.375 24.0432 25.184 24.5042 24.8441 24.8441C24.5042 25.184 24.0432 25.375 23.5625 25.375H19.3333M23.5625 21.75C24.0432 21.75 24.5042 21.559 24.8441 21.2191C25.184 20.8792 25.375 20.4182 25.375 19.9375C25.375 19.4568 25.184 18.9958 24.8441 18.6559C24.5042 18.316 24.0432 18.125 23.5625 18.125H19.3333M7.25 25.375V22.9583C7.25 21.6765 7.75922 20.4471 8.66565 19.5407C9.57208 18.6342 10.8015 18.125 12.0833 18.125H15.7083M9.66667 8.45833C9.66667 9.74021 10.1759 10.9696 11.0823 11.876C11.9887 12.7824 13.2181 13.2917 14.5 13.2917C15.7819 13.2917 17.0113 12.7824 17.9177 11.876C18.8241 10.9696 19.3333 9.74021 19.3333 8.45833C19.3333 7.17645 18.8241 5.94708 17.9177 5.04065C17.0113 4.13422 15.7819 3.625 14.5 3.625C13.2181 3.625 11.9887 4.13422 11.0823 5.04065C10.1759 5.94708 9.66667 7.17645 9.66667 8.45833Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BusinessplanIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.2917 7.25C13.2917 8.21141 13.9282 9.13344 15.0612 9.81326C16.1943 10.4931 17.731 10.875 19.3333 10.875C20.9357 10.875 22.4724 10.4931 23.6054 9.81326C24.7385 9.13344 25.375 8.21141 25.375 7.25M13.2917 12.0833V7.25C13.2917 6.28859 13.9282 5.36656 15.0612 4.68674C16.1943 4.00692 17.731 3.625 19.3333 3.625C20.9357 3.625 22.4724 4.00692 23.6054 4.68674C24.7385 5.36656 25.375 6.28859 25.375 7.25M25.375 7.25V12.0833M13.2917 12.0833C13.2917 14.0855 15.9971 15.7083 19.3333 15.7083C22.6695 15.7083 25.375 14.0855 25.375 12.0833M13.2917 12.0833V16.9167M25.375 12.0833V16.9167M13.2917 16.9167C13.2917 18.9189 15.9971 20.5417 19.3333 20.5417C22.6695 20.5417 25.375 18.9189 25.375 16.9167M13.2917 16.9167V21.75C13.2917 23.7522 15.9971 25.375 19.3333 25.375C22.6695 25.375 25.375 23.7522 25.375 21.75V16.9167M8.45833 10.875H5.4375C4.9568 10.875 4.49578 11.066 4.15587 11.4059C3.81596 11.7458 3.625 12.2068 3.625 12.6875C3.625 13.1682 3.81596 13.6292 4.15587 13.9691C4.49578 14.309 4.9568 14.5 5.4375 14.5H6.64583C7.12654 14.5 7.58756 14.691 7.92746 15.0309C8.26737 15.3708 8.45833 15.8318 8.45833 16.3125C8.45833 16.7932 8.26737 17.2542 7.92746 17.5941C7.58756 17.934 7.12654 18.125 6.64583 18.125H3.625M6.04167 18.125V19.3333M6.04167 9.66667V10.875"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WorldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.34997 10.875H24.65M4.34997 18.125H24.65M13.8958 3.625C11.8602 6.88703 10.781 10.6549 10.781 14.5C10.781 18.3451 11.8602 22.113 13.8958 25.375M15.1042 3.625C17.1398 6.88703 18.219 10.6549 18.219 14.5C18.219 18.3451 17.1398 22.113 15.1042 25.375M3.625 14.5C3.625 15.9281 3.90629 17.3423 4.45281 18.6617C4.99933 19.9811 5.80038 21.1799 6.81021 22.1898C7.82005 23.1996 9.0189 24.0007 10.3383 24.5472C11.6577 25.0937 13.0719 25.375 14.5 25.375C15.9281 25.375 17.3423 25.0937 18.6617 24.5472C19.9811 24.0007 21.1799 23.1996 22.1898 22.1898C23.1996 21.1799 24.0007 19.9811 24.5472 18.6617C25.0937 17.3423 25.375 15.9281 25.375 14.5C25.375 11.6158 24.2292 8.84967 22.1898 6.81021C20.1503 4.77076 17.3842 3.625 14.5 3.625C11.6158 3.625 8.84967 4.77076 6.81021 6.81021C4.77076 8.84967 3.625 11.6158 3.625 14.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UsersGroupIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.66667 25.375V24.1667C9.66667 23.5257 9.92128 22.911 10.3745 22.4578C10.8277 22.0046 11.4424 21.75 12.0833 21.75H16.9167C17.5576 21.75 18.1723 22.0046 18.6255 22.4578C19.0787 22.911 19.3333 23.5257 19.3333 24.1667V25.375M20.5417 12.0833H22.9583C23.5993 12.0833 24.214 12.3379 24.6672 12.7912C25.1204 13.2444 25.375 13.8591 25.375 14.5V15.7083M3.625 15.7083V14.5C3.625 13.8591 3.87961 13.2444 4.33283 12.7912C4.78604 12.3379 5.40073 12.0833 6.04167 12.0833H8.45833M12.0833 15.7083C12.0833 16.3493 12.3379 16.964 12.7912 17.4172C13.2444 17.8704 13.8591 18.125 14.5 18.125C15.1409 18.125 15.7556 17.8704 16.2088 17.4172C16.6621 16.964 16.9167 16.3493 16.9167 15.7083C16.9167 15.0674 16.6621 14.4527 16.2088 13.9995C15.7556 13.5463 15.1409 13.2917 14.5 13.2917C13.8591 13.2917 13.2444 13.5463 12.7912 13.9995C12.3379 14.4527 12.0833 15.0674 12.0833 15.7083ZM18.125 6.04167C18.125 6.68261 18.3796 7.2973 18.8328 7.75051C19.286 8.20372 19.9007 8.45833 20.5417 8.45833C21.1826 8.45833 21.7973 8.20372 22.2505 7.75051C22.7037 7.2973 22.9583 6.68261 22.9583 6.04167C22.9583 5.40073 22.7037 4.78604 22.2505 4.33283C21.7973 3.87961 21.1826 3.625 20.5417 3.625C19.9007 3.625 19.286 3.87961 18.8328 4.33283C18.3796 4.78604 18.125 5.40073 18.125 6.04167ZM6.04167 6.04167C6.04167 6.68261 6.29628 7.2973 6.74949 7.75051C7.2027 8.20372 7.81739 8.45833 8.45833 8.45833C9.09927 8.45833 9.71396 8.20372 10.1672 7.75051C10.6204 7.2973 10.875 6.68261 10.875 6.04167C10.875 5.40073 10.6204 4.78604 10.1672 4.33283C9.71396 3.87961 9.09927 3.625 8.45833 3.625C7.81739 3.625 7.2027 3.87961 6.74949 4.33283C6.29628 4.78604 6.04167 5.40073 6.04167 6.04167Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ICONS: Record<
  string,
  (props: { className?: string }) => React.ReactElement
> = {
  "user-bitcoin": UserBitcoinIcon,
  businessplan: BusinessplanIcon,
  world: WorldIcon,
  "users-group": UsersGroupIcon,
};

export default function KeyStatsSection({
  data,
  className,
}: {
  data: KeyStatsSectionData;
  className?: string;
}) {
  const items = data?.items ?? [];
  if (!items.length) return null;

  return (
    <section
      className={cn(
        "w-full bg-[#F4F5F5] py-[6rem] lg:py-[18rem] overflow-x-auto",
        className,
      )}
    >
      <div className="mx-auto lg:px-0 px-[4rem] w-[min(140rem,calc(100%-4rem))] lg:w-[min(140rem,calc(100%-8rem))]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease }}
          className="flex flex-col lg:flex-row lg:items-stretch lg:justify-between"
        >
          {items.map((item, i) => {
            const IconComponent = item.icon ? ICONS[item.icon] : null;
            return (
              <React.Fragment key={item._key ?? i}>
                {i > 0 && (
                  <div className="lg:w-px lg:h-auto h-px w-full shrink-0 self-stretch bg-black/10" />
                )}
                {/* Mobile: horizontal row — Desktop: vertical column */}
                <div className="flex items-center gap-[4.25rem] py-[3.5rem] lg:py-0 lg:flex-col lg:items-start lg:gap-[4.25rem] shrink-0">
                  {IconComponent && (
                    <IconComponent className="w-[3.625rem] h-[3.625rem] shrink-0 text-[#228E71]" />
                  )}
                  <div className="flex flex-col gap-[1.75rem] lg:gap-[3rem] whitespace-nowrap">
                    {item.value && (
                      <p className="mb-0 font-geist font-normal text-[4rem] lg:text-[6rem] leading-[1.4] tracking-[-0.2rem] lg:tracking-[-0.3rem] text-black">
                        {item.value}
                      </p>
                    )}
                    {item.label && (
                      <p className="mb-0 font-geist font-medium text-[2rem] leading-[1.4] text-black/80">
                        {item.label}
                      </p>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
